import React, { useState, useEffect } from 'react';
import { Activity, Battery, Gauge, Thermometer, Zap, AlertTriangle, Shield, Cpu, TrendingUp, BarChart3, ChevronDown, ChevronUp } from 'lucide-react';
import CircularGauge from './CircularGauge';
import CellHeatmap from './CellHeatmap';
import './KPIDashboard.css';

const KPIDashboard = ({ vehicleData, isVisible, onClose }) => {
  const [expandedSections, setExpandedSections] = useState({});
  const [realtimeData, setRealtimeData] = useState({
    voltageHistory: [],
    tempHistory: [],
    powerFlow: 0
  });

  // Simulate real-time data updates
  useEffect(() => {
    if (!isVisible) return;

    const interval = setInterval(() => {
      setRealtimeData(prev => {
        const newVoltageHistory = [...prev.voltageHistory, vehicleData.battery.voltage].slice(-60);
        const newTempHistory = [...prev.tempHistory, vehicleData.battery.temperature.pack].slice(-60);

        return {
          voltageHistory: newVoltageHistory,
          tempHistory: newTempHistory,
          powerFlow: vehicleData.battery.current || -12.5
        };
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isVisible, vehicleData]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Calculate KPIs
  const kpis = {
    battery: {
      soc: vehicleData.battery.soc,
      voltage: vehicleData.battery.voltage,
      temperature: vehicleData.battery.temperature.pack,
      range: vehicleData.battery.range?.estimated || vehicleData.battery.range || 312,
      health: vehicleData.battery.soh,
      cycles: vehicleData.battery.cycles || 487,
      insulation: 3.1, // MΩ
      cellImbalance: vehicleData.battery.cells?.imbalance || 0.09
    },
    motor: {
      temperature: vehicleData.motor.temperature,
      rpm: vehicleData.motor.rpm || 0,
      torque: vehicleData.motor.torque || 0,
      efficiency: vehicleData.motor.efficiency || 94.2
    },
    safety: {
      traStatus: 'GREEN',
      traScore: 0.12,
      hvSystemActive: true,
      servicePlugConnected: true,
      interlocksOk: true,
      activeAlerts: vehicleData.alerts.length
    },
    agents: {
      diagnostic: { status: 'IDLE', queries: 1247, avgResponseTime: 2.3, accuracy: 94.7 },
      sensor: { status: 'ACTIVE', dataRate: 250, latency: 45 },
      technician: { status: 'IDLE' }
    }
  };

  // Helper function to get status color
  const getStatusColor = (value, thresholds) => {
    if (value < thresholds.critical) return '#DF6259';
    if (value < thresholds.warning) return '#C9922F';
    if (value < thresholds.good) return '#35A877';
    return '#66B4C4';
  };

  const getSOCColor = (soc) => {
    if (soc < 15) return '#DF6259';
    if (soc < 20) return '#C9922F';
    if (soc < 80) return '#35A877';
    return '#66B4C4';
  };

  const getTRAColor = (status) => {
    switch(status) {
      case 'GREEN': return '#35A877';
      case 'ORANGE': return '#C9922F';
      case 'RED': return '#DF6259';
      default: return '#35A877';
    }
  };

  if (!isVisible) return null;

  return (
    <div className="kpi-dashboard-overlay fade-in">
      <div className="kpi-dashboard-container slide-in-right">
        {/* Header */}
        <div className="kpi-header">
          <div className="kpi-header-content">
            <div className="kpi-title-section">
              <BarChart3 className="kpi-header-icon" />
              <h2>Performance analytics</h2>
            </div>
            <div className="kpi-subtitle">Real-time KPI monitoring and analysis</div>
          </div>
          <button className="kpi-close-btn" onClick={onClose}>✕</button>
        </div>

        {/* Scrollable Content */}
        <div className="kpi-content">
          {/* PRIMARY KPIs Section */}
          <section className="kpi-section">
            <div className="kpi-section-header">
              <h3>Primary metrics</h3>
              <span className="kpi-section-badge">Live</span>
            </div>
            <div className="kpi-grid-primary">
              {/* SOC Card */}
              <div className="kpi-card kpi-card-highlight">
                <div className="kpi-card-header">
                  <Battery className="kpi-card-icon" style={{ color: getSOCColor(kpis.battery.soc) }} />
                  <span className="kpi-card-label">State of Charge</span>
                </div>
                <div className="kpi-card-body">
                  <CircularGauge
                    value={kpis.battery.soc}
                    max={100}
                    size={100}
                    strokeWidth={8}
                    color={getSOCColor(kpis.battery.soc)}
                    label={`${kpis.battery.soc}%`}
                  />
                  <div className="kpi-card-meta">
                    <span className="kpi-meta-item">Range: {kpis.battery.range} km</span>
                  </div>
                </div>
              </div>

              {/* Voltage Card */}
              <div className="kpi-card">
                <div className="kpi-card-header">
                  <Zap className="kpi-card-icon" style={{ color: '#66B4C4' }} />
                  <span className="kpi-card-label">Pack Voltage</span>
                </div>
                <div className="kpi-card-body">
                  <div className="kpi-value-large" style={{ color: '#66B4C4' }}>
                    {kpis.battery.voltage.toFixed(1)}
                    <span className="kpi-unit">V</span>
                  </div>
                  <div className="kpi-card-meta">
                    <span className="kpi-meta-item">Δ Cell: {kpis.battery.cellImbalance}V</span>
                  </div>
                </div>
              </div>

              {/* Temperature Card */}
              <div className="kpi-card">
                <div className="kpi-card-header">
                  <Thermometer className="kpi-card-icon" style={{ color: '#C9922F' }} />
                  <span className="kpi-card-label">Battery Temp</span>
                </div>
                <div className="kpi-card-body">
                  <div className="kpi-value-large" style={{ color: '#C9922F' }}>
                    {kpis.battery.temperature.toFixed(1)}
                    <span className="kpi-unit">°C</span>
                  </div>
                  <div className="kpi-card-meta">
                    <span className="kpi-meta-item">Motor: {kpis.motor.temperature}°C</span>
                  </div>
                </div>
              </div>

              {/* Health Card */}
              <div className="kpi-card">
                <div className="kpi-card-header">
                  <Activity className="kpi-card-icon" style={{ color: '#35A877' }} />
                  <span className="kpi-card-label">Battery Health</span>
                </div>
                <div className="kpi-card-body">
                  <CircularGauge
                    value={kpis.battery.health}
                    max={100}
                    size={100}
                    strokeWidth={8}
                    color="#35A877"
                    label={`${kpis.battery.health}%`}
                  />
                  <div className="kpi-card-meta">
                    <span className="kpi-meta-item">Cycles: {kpis.battery.cycles}</span>
                  </div>
                </div>
              </div>

              {/* Range Card */}
              <div className="kpi-card">
                <div className="kpi-card-header">
                  <TrendingUp className="kpi-card-icon" style={{ color: '#66B4C4' }} />
                  <span className="kpi-card-label">Estimated Range</span>
                </div>
                <div className="kpi-card-body">
                  <div className="kpi-value-large" style={{ color: '#66B4C4' }}>
                    {kpis.battery.range}
                    <span className="kpi-unit">km</span>
                  </div>
                  <div className="kpi-range-bars">
                    <div className="kpi-range-bar">
                      <span className="kpi-range-label">Estimated</span>
                      <div className="kpi-range-track">
                        <div className="kpi-range-fill" style={{ width: `${(kpis.battery.range / 437) * 100}%`, background: '#66B4C4' }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Motor Efficiency Card */}
              <div className="kpi-card">
                <div className="kpi-card-header">
                  <Gauge className="kpi-card-icon" style={{ color: '#66B4C4' }} />
                  <span className="kpi-card-label">Motor Efficiency</span>
                </div>
                <div className="kpi-card-body">
                  <div className="kpi-value-large" style={{ color: '#66B4C4' }}>
                    {kpis.motor.efficiency}
                    <span className="kpi-unit">%</span>
                  </div>
                  <div className="kpi-card-meta">
                    <span className="kpi-meta-item">RPM: {kpis.motor.rpm}</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* REAL-TIME VISUALIZATIONS Section */}
          <section className="kpi-section">
            <div className="kpi-section-header">
              <h3>Live metrics</h3>
              <span className="kpi-section-badge">1s Update</span>
            </div>
            <div className="kpi-grid-visualizations">
              {/* Voltage History */}
              <div className="kpi-viz-card">
                <div className="kpi-viz-header">
                  <span className="kpi-viz-title">Battery Voltage Trend</span>
                  <span className="kpi-viz-subtitle">Last 60 seconds</span>
                </div>
                <div className="kpi-viz-chart">
                  <div className="kpi-mini-chart">
                    {realtimeData.voltageHistory.map((v, i) => (
                      <div
                        key={i}
                        className="kpi-chart-bar"
                        style={{
                          height: `${((v - 340) / 60) * 100}%`,
                          background: '#66B4C4'
                        }}
                      ></div>
                    ))}
                  </div>
                  <div className="kpi-chart-label">
                    Current: {vehicleData.battery.voltage.toFixed(1)}V
                  </div>
                </div>
              </div>

              {/* Temperature Trend */}
              <div className="kpi-viz-card">
                <div className="kpi-viz-header">
                  <span className="kpi-viz-title">Temperature Trend</span>
                  <span className="kpi-viz-subtitle">Battery Pack</span>
                </div>
                <div className="kpi-viz-chart">
                  <div className="kpi-mini-chart">
                    {realtimeData.tempHistory.map((t, i) => (
                      <div
                        key={i}
                        className="kpi-chart-bar"
                        style={{
                          height: `${((t - 20) / 20) * 100}%`,
                          background: '#C9922F'
                        }}
                      ></div>
                    ))}
                  </div>
                  <div className="kpi-chart-label">
                    Current: {vehicleData.battery.temperature.pack.toFixed(1)}°C
                  </div>
                </div>
              </div>

              {/* Power Flow */}
              <div className="kpi-viz-card">
                <div className="kpi-viz-header">
                  <span className="kpi-viz-title">Power Flow</span>
                  <span className="kpi-viz-subtitle">Real-time</span>
                </div>
                <div className="kpi-power-flow">
                  <div className="kpi-power-value" style={{ color: realtimeData.powerFlow < 0 ? '#DF6259' : '#35A877' }}>
                    {realtimeData.powerFlow.toFixed(1)}
                    <span className="kpi-unit">kW</span>
                  </div>
                  <div className="kpi-power-label">
                    {realtimeData.powerFlow < 0 ? 'Discharging' : 'Charging'}
                  </div>
                  <div className="kpi-power-bar">
                    <div className="kpi-power-track">
                      <div
                        className="kpi-power-fill"
                        style={{
                          width: `${Math.abs(realtimeData.powerFlow) / 100 * 100}%`,
                          background: realtimeData.powerFlow < 0 ? '#DF6259' : '#35A877'
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Cell Voltage Heatmap */}
              <div className="kpi-viz-card kpi-viz-card-wide">
                <div className="kpi-viz-header">
                  <span className="kpi-viz-title">Cell Voltage Distribution</span>
                  <span className="kpi-viz-subtitle">126 Cells</span>
                </div>
                <div className="kpi-heatmap-container">
                  <CellHeatmap
                    cells={vehicleData.battery.cells?.voltages || Array(126).fill(3.65)}
                    highlightedCells={[]}
                    compact={true}
                  />
                </div>
              </div>
            </div>
          </section>

          {/* SAFETY STATUS Section */}
          <section className="kpi-section">
            <div className="kpi-section-header">
              <h3>Safety and diagnostics</h3>
              <span className="kpi-section-badge critical">Critical</span>
            </div>
            <div className="kpi-grid-safety">
              {/* Thermal Runaway Status */}
              <div className="kpi-safety-card">
                <div className="kpi-safety-header">
                  <AlertTriangle className="kpi-safety-icon" style={{ color: getTRAColor(kpis.safety.traStatus) }} />
                  <span className="kpi-safety-title">Thermal Runaway</span>
                </div>
                <div className="kpi-safety-body">
                  <div className="kpi-traffic-light">
                    <div className={`kpi-traffic-indicator kpi-traffic-${kpis.safety.traStatus.toLowerCase()}`}>
                      {kpis.safety.traStatus}
                    </div>
                  </div>
                  <div className="kpi-safety-meta">
                    Risk Score: {(kpis.safety.traScore * 100).toFixed(1)}%
                  </div>
                </div>
              </div>

              {/* HV Safety */}
              <div className="kpi-safety-card">
                <div className="kpi-safety-header">
                  <Shield className="kpi-safety-icon" style={{ color: '#35A877' }} />
                  <span className="kpi-safety-title">HV System</span>
                </div>
                <div className="kpi-safety-body">
                  <div className="kpi-safety-checklist">
                    <div className="kpi-safety-item">
                      <span className={`kpi-safety-dot ${kpis.safety.hvSystemActive ? 'active' : ''}`}></span>
                      <span>System Active</span>
                    </div>
                    <div className="kpi-safety-item">
                      <span className={`kpi-safety-dot ${kpis.safety.servicePlugConnected ? 'active' : ''}`}></span>
                      <span>Service Plug OK</span>
                    </div>
                    <div className="kpi-safety-item">
                      <span className={`kpi-safety-dot ${kpis.safety.interlocksOk ? 'active' : ''}`}></span>
                      <span>Interlocks OK</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Insulation Resistance */}
              <div className="kpi-safety-card">
                <div className="kpi-safety-header">
                  <Zap className="kpi-safety-icon" style={{ color: '#66B4C4' }} />
                  <span className="kpi-safety-title">Insulation</span>
                </div>
                <div className="kpi-safety-body">
                  <div className="kpi-value-large" style={{ color: '#66B4C4' }}>
                    {kpis.battery.insulation.toFixed(1)}
                    <span className="kpi-unit">MΩ</span>
                  </div>
                  <div className="kpi-safety-meta">
                    Status: Excellent
                  </div>
                </div>
              </div>

              {/* Active Alerts */}
              <div className="kpi-safety-card">
                <div className="kpi-safety-header">
                  <AlertTriangle className="kpi-safety-icon" style={{ color: kpis.safety.activeAlerts > 0 ? '#C9922F' : '#35A877' }} />
                  <span className="kpi-safety-title">Active Alerts</span>
                </div>
                <div className="kpi-safety-body">
                  <div className="kpi-value-large" style={{ color: kpis.safety.activeAlerts > 0 ? '#C9922F' : '#35A877' }}>
                    {kpis.safety.activeAlerts}
                  </div>
                  <div className="kpi-safety-meta">
                    {kpis.safety.activeAlerts > 0 ? 'Warnings Present' : 'All Clear'}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* AI AGENT STATUS Section */}
          <section className="kpi-section">
            <div className="kpi-section-header">
              <h3>Diagnostic agents</h3>
              <span className="kpi-section-badge">System Health</span>
            </div>
            <div className="kpi-grid-agents">
              {/* Diagnostic LLM Agent */}
              <div className="kpi-agent-card">
                <div className="kpi-agent-header">
                  <Cpu className="kpi-agent-icon" />
                  <div className="kpi-agent-info">
                    <span className="kpi-agent-title">Diagnostic LLM</span>
                    <span className={`kpi-agent-status status-${kpis.agents.diagnostic.status.toLowerCase()}`}>
                      {kpis.agents.diagnostic.status}
                    </span>
                  </div>
                </div>
                <div className="kpi-agent-stats">
                  <div className="kpi-agent-stat">
                    <span className="kpi-agent-stat-label">Queries</span>
                    <span className="kpi-agent-stat-value">{kpis.agents.diagnostic.queries}</span>
                  </div>
                  <div className="kpi-agent-stat">
                    <span className="kpi-agent-stat-label">Avg Response</span>
                    <span className="kpi-agent-stat-value">{kpis.agents.diagnostic.avgResponseTime}s</span>
                  </div>
                  <div className="kpi-agent-stat">
                    <span className="kpi-agent-stat-label">Accuracy</span>
                    <span className="kpi-agent-stat-value">{kpis.agents.diagnostic.accuracy}%</span>
                  </div>
                </div>
              </div>

              {/* Sensor Processor Agent */}
              <div className="kpi-agent-card">
                <div className="kpi-agent-header">
                  <Activity className="kpi-agent-icon" />
                  <div className="kpi-agent-info">
                    <span className="kpi-agent-title">Sensor Processor</span>
                    <span className={`kpi-agent-status status-${kpis.agents.sensor.status.toLowerCase()}`}>
                      {kpis.agents.sensor.status}
                    </span>
                  </div>
                </div>
                <div className="kpi-agent-stats">
                  <div className="kpi-agent-stat">
                    <span className="kpi-agent-stat-label">Data Rate</span>
                    <span className="kpi-agent-stat-value">{kpis.agents.sensor.dataRate}/s</span>
                  </div>
                  <div className="kpi-agent-stat">
                    <span className="kpi-agent-stat-label">Latency</span>
                    <span className="kpi-agent-stat-value">{kpis.agents.sensor.latency}ms</span>
                  </div>
                </div>
              </div>

              {/* Technician Guide Agent */}
              <div className="kpi-agent-card">
                <div className="kpi-agent-header">
                  <Shield className="kpi-agent-icon" />
                  <div className="kpi-agent-info">
                    <span className="kpi-agent-title">Technician Guide</span>
                    <span className={`kpi-agent-status status-${kpis.agents.technician.status.toLowerCase()}`}>
                      {kpis.agents.technician.status}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* DETAILED METRICS - Expandable */}
          <section className="kpi-section">
            <div
              className="kpi-section-header kpi-section-expandable"
              onClick={() => toggleSection('battery')}
            >
              <h3>Battery detail</h3>
              {expandedSections.battery ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </div>
            {expandedSections.battery && (
              <div className="kpi-expanded-content fade-in">
                <div className="kpi-detail-grid">
                  <div className="kpi-detail-item">
                    <span className="kpi-detail-label">Total Capacity</span>
                    <span className="kpi-detail-value">40.5 kWh</span>
                  </div>
                  <div className="kpi-detail-item">
                    <span className="kpi-detail-label">Usable Capacity</span>
                    <span className="kpi-detail-value">38.88 kWh</span>
                  </div>
                  <div className="kpi-detail-item">
                    <span className="kpi-detail-label">Current Available</span>
                    <span className="kpi-detail-value">33.83 kWh</span>
                  </div>
                  <div className="kpi-detail-item">
                    <span className="kpi-detail-label">Min Cell Voltage</span>
                    <span className="kpi-detail-value">3.62 V</span>
                  </div>
                  <div className="kpi-detail-item">
                    <span className="kpi-detail-label">Max Cell Voltage</span>
                    <span className="kpi-detail-value">3.71 V</span>
                  </div>
                  <div className="kpi-detail-item">
                    <span className="kpi-detail-label">Avg Cell Voltage</span>
                    <span className="kpi-detail-value">3.676 V</span>
                  </div>
                </div>
              </div>
            )}
          </section>

          <section className="kpi-section">
            <div
              className="kpi-section-header kpi-section-expandable"
              onClick={() => toggleSection('motor')}
            >
              <h3>Motor performance</h3>
              {expandedSections.motor ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </div>
            {expandedSections.motor && (
              <div className="kpi-expanded-content fade-in">
                <div className="kpi-detail-grid">
                  <div className="kpi-detail-item">
                    <span className="kpi-detail-label">Max Power</span>
                    <span className="kpi-detail-value">105 kW</span>
                  </div>
                  <div className="kpi-detail-item">
                    <span className="kpi-detail-label">Max Torque</span>
                    <span className="kpi-detail-value">260 Nm</span>
                  </div>
                  <div className="kpi-detail-item">
                    <span className="kpi-detail-label">Max RPM</span>
                    <span className="kpi-detail-value">11,500</span>
                  </div>
                  <div className="kpi-detail-item">
                    <span className="kpi-detail-label">Current Power</span>
                    <span className="kpi-detail-value">0 kW</span>
                  </div>
                  <div className="kpi-detail-item">
                    <span className="kpi-detail-label">Current Torque</span>
                    <span className="kpi-detail-value">0 Nm</span>
                  </div>
                  <div className="kpi-detail-item">
                    <span className="kpi-detail-label">Lifetime Avg Efficiency</span>
                    <span className="kpi-detail-value">92.8%</span>
                  </div>
                </div>
              </div>
            )}
          </section>

          <section className="kpi-section">
            <div
              className="kpi-section-header kpi-section-expandable"
              onClick={() => toggleSection('charging')}
            >
              <h3>Charging statistics</h3>
              {expandedSections.charging ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </div>
            {expandedSections.charging && (
              <div className="kpi-expanded-content fade-in">
                <div className="kpi-detail-grid">
                  <div className="kpi-detail-item">
                    <span className="kpi-detail-label">Total Sessions</span>
                    <span className="kpi-detail-value">487</span>
                  </div>
                  <div className="kpi-detail-item">
                    <span className="kpi-detail-label">Total Energy Charged</span>
                    <span className="kpi-detail-value">19,734 kWh</span>
                  </div>
                  <div className="kpi-detail-item">
                    <span className="kpi-detail-label">Avg Session Duration</span>
                    <span className="kpi-detail-value">52 min</span>
                  </div>
                  <div className="kpi-detail-item">
                    <span className="kpi-detail-label">Fast Charge %</span>
                    <span className="kpi-detail-value">18%</span>
                  </div>
                  <div className="kpi-detail-item">
                    <span className="kpi-detail-label">Home Charge %</span>
                    <span className="kpi-detail-value">82%</span>
                  </div>
                  <div className="kpi-detail-item">
                    <span className="kpi-detail-label">Total Cost</span>
                    <span className="kpi-detail-value">₹88,803</span>
                  </div>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default KPIDashboard;
